import { useMemo, useReducer } from 'react';
import isEqual from "lodash.isequal";

/**
 * Extracts property names from initial state of reducer to allow typesafe dispatch objects
 */
export type FieldNames<T> = {
  [K in keyof T]: T[K] extends string ? K : K;
}[keyof T];

/**
 * Action type for the dispatch object to be used for typing in context
 */
export type ActionType<T> =
  | { type: 'reset' }
  | { type?: 'change'; field: FieldNames<T>; value: any };

/**
 * useCreateReducer hook creates a typed reducer with state and dispatch.
 * Provides a simple reducer pattern for managing state with field-based updates
 * and reset functionality. Uses deep equality check to prevent unnecessary updates.
 * 
 * @hook
 * @template T - Type of the state object
 * @param {Object} params - Hook parameters
 * @param {T} params.initialState - Initial state value
 * @returns {Object} Reducer state and dispatch
 * @returns {T} returns.state - Current state
 * @returns {Function} returns.dispatch - Dispatch function for state updates
 * 
 * @example
 * ```tsx
 * interface State {
 *   name: string;
 *   age: number;
 * }
 * 
 * const { state, dispatch } = useCreateReducer<State>({
 *   initialState: { name: '', age: 0 }
 * });
 * 
 * // Update a field
 * dispatch({ field: 'name', value: 'John' });
 * 
 * // Reset to initial state
 * dispatch({ type: 'reset' });
 * ```
 */
export const useCreateReducer = <T>({ initialState }: { initialState: T }) => {
  type Action =
    | { type: 'reset' }
    | { type?: 'change'; field: FieldNames<T>; value: any };

  const reducer = (state: T, action: Action) => {
    // @ts-ignore
    if (!action.type) {
      if (isEqual(state[action.field], action.value)) return state;
      return { ...state, [action.field]: action.value };
    }

    if (action.type === 'reset') return initialState;

    throw new Error();
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  return useMemo(() => ({ state, dispatch }), [state, dispatch]);
};
